import { AlertCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PRICING_PATH } from '../../constants/routes';
import { useTranslation } from '../../context/LanguageContext';

/**
 * Prompts a user whose subscription has expired to subscribe again.
 *
 * Rendered outside the dashboard's scroll container so it cannot be scrolled
 * past — an expired account cannot generate anything, so this is the one thing
 * on the page worth interrupting for. Whether it should appear at all is
 * `useRenewalReminder`'s decision, not this component's.
 */
export function RenewalReminderBanner({ onDismiss }: Readonly<{ onDismiss: () => void }>) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      role="status"
      className="flex-shrink-0 flex items-start gap-3 border-b border-amber-500/30 bg-amber-500/10 px-6 py-3"
    >
      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-400" />

      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-amber-200">{t('subscription.renewalTitle')}</p>
        <p className="text-xs text-amber-300/80 mt-0.5">{t('subscription.renewalBody')}</p>
      </div>

      <button
        type="button"
        onClick={() => navigate(PRICING_PATH)}
        className="flex-shrink-0 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-500 transition-colors"
      >
        {t('subscription.renewalAction')}
      </button>

      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('subscription.renewalDismiss')}
        title={t('subscription.renewalDismiss')}
        className="flex-shrink-0 rounded-lg p-1 text-amber-400/70 hover:text-amber-200 hover:bg-amber-500/10 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
